import { useState, useEffect } from "react";
import { HorseArt } from "@/components/game/HorseArt";
import { TimingBarGame } from "@/components/game/minigames/TimingBarGame";
import { ObstaclePathGame } from "@/components/game/minigames/ObstaclePathGame";
import { RhythmGame } from "@/components/game/minigames/RhythmGame";
import { MinigameGate } from "@/components/game/MinigameGate";
import { ReadinessPill } from "@/components/game/ReadinessMeter";
import { Trophy, Coins, Award, Sparkles, Star, Share2, X } from "lucide-react";

const GAME_FOR = { handling: TimingBarGame, trail: ObstaclePathGame, freestyle: RhythmGame };
const INSTRUCT = {
  handling: "Tap LOCK when the marker sits in the green sweet spot. Calmer, well-handled horses get a wider zone.",
  trail: "Tap GO as the bar fills into the green window for each obstacle. Miss it and your horse spooks!",
  freestyle: "Tap on every beat when the circle pulses. Keep your combo alive for bonus points!",
};
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

export const AuctionReveal = ({ auction, coins }) => {
  const [shown, setShown] = useState(1);
  useEffect(() => {
    if (shown >= auction.bids.length) return;
    const t = setTimeout(() => setShown((n) => n + 1), 600);
    return () => clearTimeout(t);
  }, [shown, auction.bids.length]);
  const done = shown >= auction.bids.length;
  return (
    <div className="mt-3 rounded-2xl border border-border bg-card p-4" data-testid="auction-result">
      <p className="font-bold text-primary">🔨 The Auction {!done && <span className="text-xs font-normal text-muted-foreground">— bidding…</span>}</p>
      <div className="mt-2 space-y-1">
        {auction.bids.slice(0, shown).map((b, i) => (
          <div key={i} data-testid={`auction-bid-${i}`}
            className={`flex animate-in fade-in slide-in-from-bottom-1 items-center justify-between text-sm duration-300 ${b.winning ? "font-extrabold text-secondary" : "text-muted-foreground"}`}>
            <span>{b.bidder}</span><span>{b.amount}🪙{b.winning ? " · SOLD!" : ""}</span>
          </div>
        ))}
      </div>
      {done && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-700"><Coins size={14} /> +{coins} coins</p>}
    </div>
  );
};

const ResultsCard = ({ save, onClose }) => {
  const r = save.results, horse = save.horse, o = save.outcome || {};
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const text = `🐴 ${horse.name} placed ${ordinal(r.placing)} of ${r.total_entries} at the 120 Days to Makeover with an overall of ${r.overall}! #BluegrassMustangMakeover`;
    try {
      if (navigator.share) { await navigator.share({ title: "120 Days to Makeover", text }); return; }
      await navigator.clipboard.writeText(text);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* user cancelled */ }
  };
  return (
    <div className="fixed inset-0 z-[86] flex items-center justify-center bg-black/60 p-4" data-testid="results-card-modal" onClick={onClose}>
      <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="relative overflow-hidden rounded-3xl border-2 border-secondary/50 bg-gradient-to-b from-primary to-primary/85 p-5 text-center text-primary-foreground shadow-2xl" data-testid="results-card">
          <button data-testid="results-card-close" onClick={onClose} className="absolute right-3 top-3 rounded-full bg-white/15 p-1"><X size={16} /></button>
          <p className="text-[11px] font-bold uppercase tracking-widest text-secondary-foreground/80">120 Days to Makeover</p>
          <div className="mx-auto mt-2 grid h-24 w-24 place-items-center rounded-full bg-white/10"><HorseArt src={horse.art_image} color={horse.color_hex} size={92} /></div>
          <h3 className="mt-2 font-display text-2xl font-extrabold">{horse.name}</h3>
          <p className="text-xs text-primary-foreground/70">Tag #{horse.tag} · {horse.hma} HMA</p>
          <div className="mt-3 rounded-2xl bg-white/10 p-3">
            <p className="font-display text-4xl font-black text-secondary-foreground">{ordinal(r.placing)}</p>
            <p className="text-xs text-primary-foreground/80">of {r.total_entries} · Overall {r.overall}</p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            {["handling", "trail", "freestyle"].map((cid) => (
              <div key={cid} className="rounded-xl bg-white/10 p-2">
                <p className="font-bold">{r.classes[cid].total}</p>
                <p className="text-[9px] uppercase text-primary-foreground/60">{cid === "handling" ? "Handling" : cid === "trail" ? "Trail" : "Freestyle"}</p>
              </div>
            ))}
          </div>
          {o.decision && <p className="mt-3 text-xs italic text-primary-foreground/80">{o.decision === "sell" ? `Sold at auction for ${o.coins_awarded}🪙` : "Kept for the stable ❤"}</p>}
        </div>
        <button data-testid="results-card-share-btn" onClick={share}
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-secondary font-bold text-white active:scale-95">
          <Share2 size={16} /> {copied ? "Copied to clipboard!" : "Share my result"}
        </button>
      </div>
    </div>
  );
};

export const Competition = ({ save, busy, onStart, onSubmit, onDecide, onNewMakeover, onVisitStable, onGameHome, onViewAlumni }) => {
  const [idx, setIdx] = useState(0);
  const [perfs, setPerfs] = useState({});
  const [scoring, setScoring] = useState(false);
  const [showCard, setShowCard] = useState(false);

  if (!save) return null;
  const horse = save.horse;

  // ---- Retired hub (after keep/sell) ----
  if (save.status === "retired") {
    const o = save.outcome || {};
    const a = o.alumni || {};
    return (
      <div className="px-4 pt-6 pb-10" data-testid="retire-hub">
        <div className="rounded-2xl border border-secondary/40 bg-secondary/10 p-5 text-center">
          <Sparkles size={30} className="mx-auto text-secondary" />
          <h2 className="mt-2 font-display text-2xl font-bold text-primary" data-testid="retire-title">
            {o.decision === "sell" ? `${horse.name} sold at auction!` : `${horse.name} is in your stable!`}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="retire-subtitle">Finished {ordinal(a.scores?.placing ?? a.placing)} · Overall {a.scores?.overall ?? a.overall}</p>
        </div>

        {o.auction && <AuctionReveal auction={o.auction} coins={o.coins_awarded} />}

        <div className="mt-5 space-y-2">
          <button data-testid="hub-new-makeover-btn" disabled={busy} onClick={onNewMakeover}
            className="h-12 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">
            {busy ? "Rounding them up…" : "Start a New Makeover"}
          </button>
          <button data-testid="view-results-card-btn" onClick={() => setShowCard(true)}
            className="h-11 w-full rounded-xl border border-border bg-card font-bold text-primary active:scale-95">View Results Card</button>
          <button data-testid="view-alumni-profile-btn" onClick={onViewAlumni}
            className="h-11 w-full rounded-xl border border-border bg-card font-bold text-primary active:scale-95">View Alumni Profile</button>
          <button data-testid="game-home-btn" onClick={onGameHome}
            className="h-11 w-full rounded-xl border border-border bg-card font-bold text-primary active:scale-95">Game Home</button>
          <p className="pt-1 text-center text-xs text-muted-foreground">This completion screen won't reopen once you leave — your horse is saved in the Alumni Book.</p>
        </div>
        {showCard && <ResultsCard save={save} onClose={() => setShowCard(false)} />}
      </div>
    );
  }

  // ---- Intro ----
  if (save.status === "ready_to_compete") {
    return (
      <div className="px-4 pt-6 pb-10" data-testid="competition-intro">
        <div className="rounded-2xl border border-secondary/40 bg-secondary/10 p-5 text-center">
          <Trophy size={34} className="mx-auto text-secondary" />
          <h2 className="mt-2 font-display text-2xl font-bold text-primary">Makeover Competition Day!</h2>
          <p className="mt-1 text-sm text-muted-foreground">{horse.name} has trained 120 days. Time to show the judges! Three classes, each a hands-on challenge.</p>
          <div className="mt-3 flex justify-center"><ReadinessPill readiness={save.readiness} /></div>
        </div>
        <div className="mt-4 space-y-2">
          {["Handling & Conditioning", "Trail", "Freestyle"].map((c, i) => (
            <div key={c} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{i + 1}</span>
              <span className="text-sm font-bold text-primary">{c}</span>
            </div>
          ))}
        </div>
        <button data-testid="enter-arena-btn" disabled={busy} onClick={onStart}
          className="mt-5 h-13 w-full rounded-xl bg-secondary py-4 font-bold text-white disabled:opacity-60 active:scale-95">
          {busy ? "Warming up…" : "Enter the Arena →"}
        </button>
      </div>
    );
  }

  // ---- Running the mini-games ----
  if (save.status === "competing" && save.competition) {
    if (scoring) {
      return <div className="px-4 pt-16 text-center text-muted-foreground" data-testid="competition-scoring">The judges are tallying their scores…</div>;
    }
    const comp = save.competition;
    const order = comp.order;
    const cid = order[idx];
    const Game = GAME_FOR[cid];
    const conf = comp.classes[cid];
    const onDone = (perf) => {
      const next = { ...perfs, [cid]: perf };
      setPerfs(next);
      if (idx + 1 >= order.length) { setScoring(true); onSubmit(next); }
      else setIdx(idx + 1);
    };
    return (
      <div className="px-4 pt-4 pb-10" data-testid="competition-arena">
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
          <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${horse.color_hex}1a` }}><HorseArt src={horse.art_image} color={horse.color_hex} size={44} /></div>
          <div>
            <p className="font-display text-lg font-bold text-primary">{conf.label}</p>
            <p className="text-xs font-semibold text-secondary" data-testid="class-progress">Class {idx + 1} of {order.length} · {horse.name}</p>
          </div>
        </div>
        <p className="mt-2 rounded-xl bg-muted/60 p-3 text-xs text-foreground/80">{INSTRUCT[cid]}</p>
        <div className="mt-3"><MinigameGate key={cid} gameKey={cid}>{() => <Game difficulty={conf.difficulty} horse={horse} onDone={onDone} />}</MinigameGate></div>
        <div className="mt-4 flex justify-center gap-2">
          {order.map((c, i) => <span key={c} className={`h-2 w-8 rounded-full ${i < idx ? "bg-secondary" : i === idx ? "bg-primary" : "bg-muted"}`} />)}
        </div>
      </div>
    );
  }

  // ---- Results + keep/sell ----
  if (save.status === "complete" && save.results) {
    const r = save.results;
    return (
      <div className="px-4 pt-4 pb-10" data-testid="competition-results">
        <div className="rounded-2xl border border-secondary/50 bg-secondary/10 p-5 text-center">
          <Award size={32} className="mx-auto text-secondary" />
          <p className="mt-1 font-display text-3xl font-extrabold text-primary" data-testid="results-placing">{ordinal(r.placing)} place</p>
          <p className="text-sm text-muted-foreground">out of {r.total_entries} · Overall <span className="font-bold text-primary" data-testid="results-overall">{r.overall}</span></p>
          <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-700"><Coins size={14} /> +{r.prize} prize coins</p>
        </div>

        <p className="mt-3 text-xs font-semibold text-muted-foreground">Judges: {r.judges.join(" · ")}</p>

        <div className="mt-2 space-y-3">
          {["handling", "trail", "freestyle"].map((cid) => {
            const c = r.classes[cid];
            return (
              <div key={cid} className="rounded-2xl border border-border bg-card p-4" data-testid={`result-class-${cid}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-primary">{c.label}</p>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-sm font-bold text-primary">{c.total}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.judge_scores.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold"><Star size={10} className="text-amber-500" /> {s}</span>
                  ))}
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">+{c.bonus} bonus</span>
                </div>
                <p className="mt-2 text-xs italic text-muted-foreground" data-testid={`judge-comment-${cid}`}>“{c.comment}”</p>
              </div>
            );
          })}
        </div>

        {r.field && (
          <div className="mt-3 rounded-2xl border border-border bg-card p-4" data-testid="results-leaderboard">
            <p className="font-bold text-primary">Final Standings</p>
            <div className="mt-2 space-y-1">
              {r.field.map((e, i) => (
                <div key={i} data-testid={e.is_player ? "leaderboard-you" : `leaderboard-${i}`}
                  className={`flex items-center justify-between text-sm ${e.is_player ? "font-extrabold text-secondary" : "text-muted-foreground"}`}>
                  <span>{i + 1}. {e.name}</span><span>{e.overall}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {r.penalty > 0 && (
          <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-[11px] font-semibold text-amber-800" data-testid="results-penalty">
            −{r.penalty} pts applied for missing core skills. Master the basics next time for a higher score!
          </p>
        )}

        <div className="mt-4 rounded-2xl border border-border bg-card p-4">
          <p className="font-display text-lg font-bold text-primary">Keep or sell {horse.name}?</p>
          <p className="mt-1 text-xs text-muted-foreground">Sell at the fictional auction for an estimated <span className="font-bold text-amber-700">{r.sale_estimate} coins</span>, or keep {horse.name} for your stable.</p>
          <div className="mt-3 flex gap-2">
            <button data-testid="keep-btn" disabled={busy} onClick={() => onDecide("keep")}
              className="h-12 flex-1 rounded-xl border-2 border-primary bg-card font-bold text-primary disabled:opacity-60 active:scale-95">Keep</button>
            <button data-testid="sell-btn" disabled={busy} onClick={() => onDecide("sell")}
              className="h-12 flex-1 rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">Sell at Auction · ~{r.sale_estimate}🪙</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
